import {
  CryptocurrenciesEnum,
  PaymentMethodEnum,
  PaymentProcessEnum,
  PaymentProviderEnum,
  PaymentStatusEnum,
} from '../../enums';
import { CRMState } from '../../store/crm';
import { NullableNumber, NullableString } from '../../util/types';
import { ENROLL_METHOD } from '../enroll';
import { AccountInfo } from '../linked-account/types';

export type PaymentsFilter = {
  payment_id?: number;
  payment_status?: PaymentStatusEnum[];
  payment_method?: PaymentMethodEnum[];
  purchase_type_ids?: PurchasePaymentTypeEnum[];
  account_owner_name?: string;
  created_by?: string;
  created_by_user_id?: number;
  account_reference_id?: string;
  accounts_id?: number[];
  amount_from?: string;
  amount_to?: string;
  settled_date_from?: string;
  settled_date_to?: string;
  account_owner_id?: number;
  is_account_owner_company?: boolean;
  date_from?: string;
  date_to?: string;
  payment_process?: number[];
  page_number: number;
  page_size: number;
  order_column: string;
  order_ascending: boolean;
}

export type GetPaymentsPayload = {
  paymentProcess: PaymentProcessEnum[];
  paymentStatus?: PaymentStatusEnum[];
  pageNumber: number;
  pageSize?: number;
  defaultPageNumber: number;
  dateOffset: number;
  // used for queueing when initially loading first 15 and then all deposits
  isQueued?: boolean;
  isAllTransactions?: boolean;
}

export type Payment = {
  payment_id: number;
  payment_status: PaymentStatusEnum;
  amount: number;
  payment_process: PaymentProcessEnum;
  payment_provider: PaymentProviderEnum;
  create_date: string;
  settled_date: string;
  created_by_user_id: number;
  created_by: string;
  payment_method: PaymentMethodEnum;
  payment_reference: string;
  currency_code: string;
  description: string;
  category: string;
  sender_email: string;
  sender_account_number: string;
  sender_sort_code: string;
  sender_iban: string;
  sender_bic: string;
  sender_name: string;
  sender_bank_name: string;
  sender_bank_country_id: string;
  sender_bank_address: string;
  recipient_email: string;
  recipient_account_number: string;
  recipient_sort_code: string;
  recipient_iban: string;
  recipient_bic: string;
  recipient_name: string;
  recipient_bank_name: string;
  recipient_bank_country_id: string;
  recipient_bank_address: string;
  account_id: number;
  account_status: number;
  account_name: string;
  account_group_id: number;
  account_created_date: string;
  account_reference_id: string;
  note: string;
  is_cash_account: boolean;
  is_account_owner_company: boolean;
  account_owner_id: number;
  account_owner_email: string;
  account_owner_name: string;
  account_owner_status: number;
  owner_residential_country_id: string;
  owner_type_behaviour: number;
  tenant_id: number;
  purchase_type_id?: PurchasePaymentTypeEnum;
  crypto_detail_rate?: number;
  recipient_crypto_wallet_address?: string;
}

/** `crm.detailedAccounts` - kept split, so the consumers do not filter by `is_cash_account`. */
export type DetailedAccounts = {
  cashAccounts: DetailedAccount[];
  tradingAccounts: DetailedAccount[];
}

export type DetailedAccount = Pick<AccountSummary,
  'account_expiration_date'
  | 'account_id'
  | 'account_status'
  | 'account_trading_plan_status'
  | 'cash_account_reference_id'
  | 'is_cash_account'
  | 'reference_id'
  | 'trading_plan_id'
>;

export type WithdrawalRequest = {
  amount: number;
  bank_details: AccountInfo
  payment_provider?: number;
  payment_process?: number;
  payment_method?: number;
  to_internal_account_id?: number;
  currency_code?: string;
  crypto_symbol?: string;
}

export type OwnerTradingAccount = {
  id: number;
  reference_id: string;
  is_cash_account: boolean;
  status: TradingAccountStatusesEnum;
  sub_type: AccountSubType;
}

export type AccountSummary = {
  account_id: number;
  reference_id: string;
  account_status: TradingAccountStatusesEnum;
  account_trading_plan_status: AccountTradingPlanStatusEnum;
  is_cash_account: boolean;
  trading_plan_id: NullableNumber;
  account_expiration_date: NullableString;
  /** The cash account this trading account draws on. Null on a cash account itself. */
  cash_account_reference_id: NullableString;
}

export enum TradingAccountStatusesEnum {
  Unspecified,
  ForApproval,
  Rejected,
  Active = 10,
  WithdrawalRestricted,
  Blocked = 20,
  BlockOrderEntry,
  BlockShortSells,
  Closed = 30,
  ClosedCancel,
  PendingCreation = 1000
}

export enum AccountSubType {
  TradingCash = 1,
  Trading = 2,
  FundCash = 10,
  MutualFund = 11,
  InstrumentCertificate = 12
}

export interface LookupInterface {
  [key: string]: string;
}

export class LookupModel {
  private types: { [key: string]: string };

  constructor(data: LookupInterface) {
    this.types = data;
  }

  getTypes(): { [key: string]: string } {
    return this.types;
  }

  extendTypes(types: { [key: string]: string }): LookupModel {
    this.types = { ...this.types, ...types };

    return this;
  }

  getTypeKeys(): string[] {
    return Object.keys(this.types).filter((key: string) => [ 'UNS', 'U', 'UNSP' ].indexOf(key) < 0);
  }

  getTypeValue(key: string): string {
    return (typeof this.types[key] !== 'undefined') ? this.types[key] : '';
  }

  removeType(key: string): void {
    if (this.types[key]) {
      delete this.types[key];
    }
  }
}

export type OnboardingQuestionMethod = ENROLL_METHOD | ((state: CRMState) => ENROLL_METHOD)

export interface LegalDeclaration {
  id: number;
  name: string;
  storage_id: string;
  template_storage_id: string;
}

export type TradingPlatform = {
  trading_platform_name: string
  trading_platform_id: number
}

export enum AccountPrefixByTradingPlatformEnum {
  HammerWeb = 'HWL',
  TenN = 'TNN',
  AlaricFunds = 'AAA',
  ACE = 'ACE', // for prod env
  EIS = 'EIS', // for dev and test env
}

export enum UserTypeRelation {
  Individual = 1,
  CompanyEmployee = 2,
  Operator = 3,
  IndividualSubordinate = 4,
}

export type AccountsSummaryRequest = {
  customerId: number,
  userTypeRelation: number,
}

export enum CapitalSubscriptionEnum {
  Capital = 'Capital'
}

export enum SubscriptionStatusEnum {
  UnSubscribed = 0,
  Subscribed = 1
}

export type ModifyOwnBirthdateRequest = {
  birthdate: string;
  user_id?: number;
}

export type SearchTradingPlansRequest = {
  page_number: number;
  page_size: number;
  order_column: string;
  order_ascending: boolean;
  tenant_id: number;
  is_demo?: boolean;
  is_default?: boolean;
  reference_id?: string;
  work_flow_depth?: number;
  trading_plan_type_enum?: TradingPlanTypeEnum;
  trading_plan_status_enum?: TradingPlanStatus;
}

export enum TradingPlanStatus {
  Inactive = 10,
  Purchasable = 20,
  Active = 30,
  Expired = 40,
  Set = 50,
  OnHold = 60,
  ActiveAndPurchasable = 70,
}

export enum AccountTradingPlanStatusEnum {
  Unspecified = 0,
  InProgress = 10,
  AwaitingReview = 20,
  Successful = 30,
  Failed = 40,
  Rejected = 50,
}

export enum TradingPlanTypeEnum {
  Demo = 10,
  Tryout = 20,
  PrimeTrading = 40,
  FundedAccount = 50,
}

export enum PaymentReturnUrlTypeEnum {
  Success = 1,
  Cancel = 2,
  Fail = 3
}

export enum PurchasePaymentTypeEnum {
  Plan = 1,
  AccountExpiryExtension = 2,
  AccountDailyLossRewind = 3,
  AutoPass = 4,
  PlanWithAutoPass = 5,
}

export enum PlanPhaseTypeEnum {
  demo = 'Demo',
  challenge = 'Challenge',
  theChallenge = 'The Challenge',
  entry = 'Entry Funded',
  pro = 'Pro Funded',
  elite = 'Elite Funded',
}

export type AutoPassRequest = {
  is_auto_pass_enabled: boolean;
}

export type AutoPassResponse = {
  individual_id: number;
  is_auto_pass_enabled: boolean;
  tenant_id: number;
} | boolean;

export type TradingPlan = {
  id: number;
  reference_id: string;
  trading_plan_status_enum: TradingPlanStatus;
  plan_type: TradingPlanTypeEnum;
  name: string;
  plan_price: number;
  plan_price_ccy: string;
  plan_price_with_auto_pass: number;
  daily_hard_bp: number;
  daily_hard_ccy: string;
  daily_loss_limit: number;
  maximum_total_loss: number;
  no_gambling_rule: boolean;
  profit_target: number;
  profit_target_amount?: number;
  daily_loss_limit_amount?: number;
  maximum_total_loss_amount?: number;
  min_trading_days_target: number;
  consistency_coefficient: number;
  consistency_coefficient_minimum: number;
  rewind_daily_loss_price: number;
  rewind_daily_loss_price_ccy: string;
  auto_pass_deactivation_price: number;
  auto_pass_deactivation_ccy: string;
  payout_split: NullableNumber;
  penalty_payout_split: NullableNumber;
  plans_accounts_activation: NullableString;
  phase: PlanPhaseTypeEnum;
  previous_trading_plan_id: NullableNumber;
  next_trading_plan_id: NullableNumber;
};

export type BasePaymentParam = {
  individual_id: number,
  payment_provider: PaymentProviderEnum,
  crypto_symbol?: CryptocurrenciesEnum,
}

export type PaymentReturnUrls = {
  return_url?: string,
  cancel_url?: string,
  fail_url?: string,
}

export type CouponPaymentParams = {
  coupon_code?: string,
  quote_reference?: string,
  idempotency_key?: string,
}

export type CreatePlanPaymentParams = Omit<BasePaymentParam, 'payment_provider'>
  & PaymentReturnUrls
  & CouponPaymentParams
  & {
    payment_provider: PaymentProviderEnum | null,
    trading_plan_id: number,
    with_auto_pass?: boolean,
    terms_and_conditions_url?: string,
  }

export type CryptoRawData = {
  crypto_wallet: string;
  crypto_symbol_name: string;
  crypto_symbol_address: string;
  amount: number;
  currency: string;
  crypto_amount: number;
  rate: number;
  qr_code: string;
  expires_at: string;
  transfer_type: number;
  payment_uri: string;
}

export type CreatePlanPaymentResponse = {
  gateway_url: string,
  payment_reference: string,
  data: string,
  raw_data: CryptoRawData | null,
}

export type CreatePurchasePaymentParam = Omit<BasePaymentParam, 'payment_provider'>
  & PaymentReturnUrls
  & CouponPaymentParams
  & {
    payment_provider: PaymentProviderEnum | null,
    account_id: number,
    purchase_type: PurchasePaymentTypeEnum,
  }

export type CouponPreviewParams = {
  individual_id: number,
  coupon_code: string,
  // The plan purchase is identified by its plan, the auto-pass and daily-loss-rewind
  // purchases by the account they apply to.
  trading_plan_id?: number,
  account_id?: number,
  purchase_type: PurchasePaymentTypeEnum,
  with_auto_pass: boolean,
  payment_provider: PaymentProviderEnum | null,
  crypto_symbol?: CryptocurrenciesEnum,
}

export type CouponPreviewResponse = {
  quote_reference: string,
  original_amount: number,
  discount_amount: number,
  final_amount: number,
  currency_code: string,
  purchase_type: string,
  valid_until_utc: string,
}

/* ----------------------------- Referrals ----------------------------- */

export enum ReferralStatusEnum {
  purchased = 'PURCHASED',
  notPurchased = 'NOT_PURCHASED',
}

export enum CommissionStatusEnum {
  available = 'AVAILABLE',
  requested = 'REQUESTED',
  paid = 'PAID',
  rejected = 'REJECTED',
}

export type ReferralSummary = {
  code: string;
  link: string;
  registrations: number;
  purchases: number;
};

export type Referral = {
  id: string;
  maskedEmail: string;
  registeredAt: string;
  commission: NullableNumber;
  status: ReferralStatusEnum;
};

export type Commission = {
  id: string;
  maskedEmail: string;
  requestId: NullableString;
  commission: number;
  status: CommissionStatusEnum;
  statusChangedAt: string;
};

export enum ReferralProfileStatusEnum {
  active = 1,
  inactive = 2,
}

export enum ReferralActivationSourceEnum {
  automatic = 1,
  manual = 2,
}

/** One balance bucket per currency - see `GET /api/v1/referral/own/profile`. */
export type ReferralProfileBalance = {
  currency: NullableString;
  unpaid: number;
  requested: number;
  paid: number;
};

/** Response of `GET /api/v1/referral/own/profile`. A 404 means the user has no profile yet. */
export type ReferralProfileResponse = {
  referral_profile_id: NullableNumber;
  individual_id: number;
  individual_email: NullableString;
  code: NullableString;
  referral_link: NullableString;
  can_invite: boolean;
  commission_percent: number;
  status: ReferralProfileStatusEnum;
  status_name: NullableString;
  activation_source: ReferralActivationSourceEnum;
  activation_source_name: NullableString;
  code_frozen: boolean;
  referred_registered_count: number;
  referred_purchased_count: number;
  balances: ReferralProfileBalance[];
  min_payout_amount: number;
  min_payout_currency: NullableString;
  payout_code: NullableString;
  payout_amount: NullableNumber;
  payout_currency: NullableString;
  can_request_payout: boolean;
  qr_enabled: boolean;
  created_at_utc: string;
};

export enum ReferralCommissionStatusEnum {
  available = 1,
  requested = 2,
  paid = 3,
  rejected = 4,
  cancelled = 5,
}

export enum ReferralPayoutStatusEnum {
  requested = 1,
  approved = 2,
  paid = 3,
  rejected = 4,
  cancelled = 5,
}

/** Paging envelope shared by the referral list endpoints. */
export type ReferralPagedResponse<T> = {
  total_pages: number;
  total_elements: number;
  page_size: number;
  page_number: number;
  elements_in_page: number;
  content: T[];
};

export type ReferralPagedRequest = {
  PageNumber?: number;
  PageSize?: number;
  OrderColumn?: string;
  OrderAscending?: boolean;
  DisablePaging?: boolean;
};

/** The part of the referral profile the app actually reads - see `crm/stripping`. */
export type ReferralProfile = {
  code: NullableString;
  referral_link: NullableString;
  can_invite: boolean;
  min_payout_amount: number;
  can_request_payout: boolean;
  referred_registered_count: number;
  referred_purchased_count: number;
  balances: ReferralProfileBalance[];
};

/** Response of `GET /api/v1/referral/own/qr`. 404 when QR is off or the user cannot invite. */
export type ReferralQrCode = {
  qr_image_base64: NullableString;
  referral_link: NullableString;
};

/** Item of `GET /api/v1/referral/own/referred-users`. */
export type ReferralReferredUser = {
  masked_email: NullableString;
  internal_reference: NullableString;
  registered_at_utc: string;
  purchase_made: boolean;
  purchase_date_utc: NullableString;
  plan_name: NullableString;
  plan_phase: NullableString;
  purchase_amount: NullableNumber;
  currency: NullableString;
  commission_amount: NullableNumber;
  commission_status: ReferralCommissionStatusEnum;
  commission_status_name: NullableString;
  payout_request_id: NullableNumber;
};

/** Item of `GET /api/v1/referral/own/commissions`. */
export type ReferralCommissionEntry = {
  id: number;
  created_at_utc: string;
  masked_referred_email: NullableString;
  internal_reference: NullableString;
  plan_name: NullableString;
  purchase_amount: number;
  currency: NullableString;
  commission_percent: number;
  commission_amount: number;
  status: ReferralCommissionStatusEnum;
  status_name: NullableString;
  payout_request_id: NullableNumber;
  referral_code_id: NullableNumber;
  referral_code: NullableString;
  payout_status: ReferralPayoutStatusEnum;
  payout_status_name: NullableString;
  status_changed_at_utc: string;
};

export type ReferralPayoutTotal = {
  currency: NullableString;
  amount: number;
};

export type ReferralPayoutItem = {
  commission_entry_id: number;
  referred_individual_id: number;
  payment_reference: NullableString;
  plan_name: NullableString;
  purchase_amount: number;
  commission_amount: NullableNumber;
  currency_code: NullableString;
  status: ReferralCommissionStatusEnum;
};

/** Item of `GET /api/v1/referral/own/payout-requests` and body of the POST response. */
export type ReferralPayoutRequest = {
  id: number;
  referral_profile_id: number;
  referrer_email: NullableString;
  referrer_code: NullableString;
  referral_code_id: NullableNumber;
  status: ReferralPayoutStatusEnum;
  status_name: NullableString;
  requested_at_utc: string;
  totals: ReferralPayoutTotal[];
  entry_count: number;
  approved_at_utc: NullableString;
  paid_at_utc: NullableString;
  payment_reference: NullableString;
  rejected_at_utc: NullableString;
  rejection_reason: NullableString;
  cancelled_at_utc: NullableString;
  cancellation_reason: NullableString;
  items: ReferralPayoutItem[];
};

/** Body of `POST /api/v1/referral/own/payout-request`. */
export type CreateReferralPayoutRequest = {
  referral_code: string;
};
